import { PayloadAction, createSelector, createSlice,  } from '@reduxjs/toolkit';
import { RootState } from '@store/create-store';
import { ChartMarkerConfiguration } from '@scenario/models/markers.model';
import { ChartMarkers } from '@scenario/enums/chart/chart-markers.enum';
import { HistoricalDataMode } from '@scenario/enums/historical-data-mode.enum';

export interface AppState {
  advancedMode: boolean;
  currencySaved: boolean;
  chartMarkersConfiguration: ChartMarkerConfiguration;
  historicalDataMode: HistoricalDataMode;
}

const initialState: AppState = {
  advancedMode: false,
  currencySaved: false,
  chartMarkersConfiguration: {
    zone : ChartMarkers.COLOR,
    source : ChartMarkers.SHAPE,
    scenario : ChartMarkers.LINE_TYPE,
    publicationDate : ChartMarkers.THICKNESS,
  },
  historicalDataMode: HistoricalDataMode.FORECAST,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    enableAdvancedMode: state => {
      state.advancedMode = true;
    },
    disableAdvancedMode: state => {
      state.advancedMode = false;
    },
    currencySaved: (state, action) => {
      state.currencySaved = action.payload;
    },
    saveZoneChartMarker: (state, action: PayloadAction<ChartMarkers>) => {
      state.chartMarkersConfiguration.zone = action.payload;
    },
    saveSourceChartMarker: (state, action: PayloadAction<ChartMarkers>) => {
      state.chartMarkersConfiguration.source = action.payload;
    },
    resetChartMarkersConfiguration: state => {
      state.chartMarkersConfiguration = initialState.chartMarkersConfiguration;
    },
    saveHistoricalDataMode: (state, action: PayloadAction<HistoricalDataMode>) => {
      state.historicalDataMode = action.payload;
    },
  },
});

export const selectAppMode = createSelector(
  (state: RootState) => state,
  (state): AppState => ({
    advancedMode: state.app.advancedMode,
    currencySaved: state.app.currencySaved,
    chartMarkersConfiguration: state.app.chartMarkersConfiguration,
    historicalDataMode: state.app.historicalDataMode,
  }),
);

export const selectChartMarkersConfiguration = createSelector(
  (state: RootState) => state,
  (state): ChartMarkerConfiguration =>  state.app.chartMarkersConfiguration
);

export const selectSelectedChartMarkers = createSelector(
  (state: RootState) => state,
  (state): Array<ChartMarkers> => {
    const uniqueMarkers = new Set<ChartMarkers>(Object.values(state.app.chartMarkersConfiguration));
    return Array.from(uniqueMarkers).filter((chartMarker) => chartMarker !== ChartMarkers.NONE);
  }
);

export const selectHistoricalDataMode = createSelector(
  (state: RootState) => state,
  (state): HistoricalDataMode => state.app.historicalDataMode,
);

export const { enableAdvancedMode, disableAdvancedMode, currencySaved, saveZoneChartMarker, saveSourceChartMarker, resetChartMarkersConfiguration, saveHistoricalDataMode } = appSlice.actions;

export default appSlice.reducer;
